import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  CompanySegmentType,
  ICompanyProfileDocuments,
  ICompanyProvidersResponse,
  ICompanySegmentAlias,
} from '../sdk/company/company.types'
import { INetworkResponse } from '../sdk/network/network.type'
import { sdk } from '../services/sdk'

import { AuthContext } from './auth'

interface GlobalContextData {
  companySegmentType: CompanySegmentType
  allCompanySegmentType: CompanySegmentType
  expiringDocuments: ICompanyProfileDocuments[] | null
  companyProviders: ICompanyProvidersResponse[] | null
  blockchainNetwork: INetworkResponse | null
  actions: {
    fetchProviders: () => Promise<ICompanyProvidersResponse[]>
  }
}

interface GlobalDataProps {
  children: ReactNode
}

export const GlobalDataContext = createContext({} as GlobalContextData)

export function GlobalDataProvider(props: GlobalDataProps) {
  const [companyProviders, setCompanyProviders] = useState<
    ICompanyProvidersResponse[] | null
  >(null)
  const [companySegmentType, setCompanySegmentType] =
    useState<CompanySegmentType>({} as CompanySegmentType)
  const [allCompanySegmentType, setAllCompanySegmentType] =
    useState<CompanySegmentType>({} as CompanySegmentType)
  const [expiringDocuments, setExpiringDocuments] = useState<
    ICompanyProfileDocuments[] | null
  >(null)
  const { user } = useContext(AuthContext)
  const [blockchainNetwork, setBlockchainNetwork] =
    useState<INetworkResponse | null>(null)

  const fetchExpiringDocuments = useCallback(async () => {
    try {
      const response = await sdk.company.getExpiringDocuments()
      setExpiringDocuments(response)
    } catch (error) {}
  }, [])

  const fetchAllSegments = useCallback(async () => {
    const result = await sdk.company.getAllSegments()

    setAllCompanySegmentType(transformToNameObjects(result))
  }, [])
  const fetchSegments = useCallback(async () => {
    const result = await sdk.company.getSegments()
    setCompanySegmentType(transformToNameObjects(result))
  }, [])

  const fetchProviders = async () => {
    try {
      const response = await sdk.company.getFirsttierProviders()
      setCompanyProviders(response.data)
      return response.data
    } catch (error) {
      return []
    }
  }

  function transformToNameObjects(array: ICompanySegmentAlias[]) {
    return array.reduce((acc: any, item: any) => {
      acc[item.name] = { ...item }
      return acc
    }, {})
  }

  const fetchBlockchainNetwork = useCallback(async () => {
    try {
      const networkResponse = await sdk.network.getNetworkByCompanyId()

      const explorerLoginResponse = await loginExplorer(user?.user.id)

      const updatedNetworkResponse = {
        ...networkResponse,
        explorerLogin: explorerLoginResponse,
      }

      setBlockchainNetwork(updatedNetworkResponse)
    } catch (err) {}
  }, [user])

  const loginExplorer = async (userId: string | undefined) => {
    return await sdk.network.getExploreUserByCompanyId(userId)
  }

  useEffect(() => {
    if (user) {
      fetchSegments()
      fetchAllSegments()
      fetchExpiringDocuments()
      fetchProviders()
      fetchBlockchainNetwork()
    }
  }, [user, fetchSegments])

  return (
    <GlobalDataContext.Provider
      value={{
        allCompanySegmentType,
        companySegmentType,
        expiringDocuments,
        companyProviders,
        blockchainNetwork,
        actions: {
          fetchProviders,
        },
      }}
    >
      {props.children}
    </GlobalDataContext.Provider>
  )
}
